import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { Url, URLS } from '@wix/da-url';
import { BiData, BiContextProvidedValue } from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { editDeviation, shareDeviationToPost } from '../../redux/deviations/actionCreators';
import { DeviationResourceType } from '../../types/deviation';
import { useVerification } from '../../VerificationPopup/withVerification';
import { useCopyDeviationLinkClick } from '../../utils/hooks/useCopyLinkClick';
import { deleteTier } from '../redux/creator/actions';
import { getIsReduxInitialized as getIsDiscordReduxInitialized } from '../../Discord/redux/selectors';
import { useSubscriptionTier } from '../../utils/hooks/useSubscriptionTier';
import { subscriberConnect } from '../../Discord/redux/actions';
function useEditTierAction(deviation) {
    const { t } = useTranslation();
    const dispatchEditDeviation = useAction(editDeviation);
    const handleEditTier = useCallback(()=>{
        dispatchEditDeviation({
            deviation
        });
    }, [
        dispatchEditDeviation,
        deviation
    ]);
    return {
        label: t('common.edit'),
        onClick: handleEditTier,
        biData: BiData({
            evid: 536,
            typeid: DeviationResourceType.TIER,
            itemid: deviation.deviationId
        })
    };
}
function useDeleteTierAction(deviation, afterActionUrl) {
    var _deviation_tier_stats, _deviation_tier;
    const { t } = useTranslation();
    const dispatchDeleteTier = useAction(deleteTier);
    const handleDeleteTier = useCallback(()=>{
        dispatchDeleteTier({
            deviation,
            redirectTo: afterActionUrl
        });
    }, [
        dispatchDeleteTier,
        deviation,
        afterActionUrl
    ]);
    var _deviation_tier_stats_subscribers;
    return {
        label: t('common.delete'),
        onClick: handleDeleteTier,
        biData: BiData({
            evid: 541,
            typeid: DeviationResourceType.TIER,
            itemid: deviation.deviationId,
            has_subscribers: ((_deviation_tier_stats_subscribers = (_deviation_tier = deviation.tier) === null || _deviation_tier === void 0 ? void 0 : (_deviation_tier_stats = _deviation_tier.stats) === null || _deviation_tier_stats === void 0 ? void 0 : _deviation_tier_stats.subscribers) !== null && _deviation_tier_stats_subscribers !== void 0 ? _deviation_tier_stats_subscribers : 0) > 0 ? 1 : 0
        })
    };
}
function useGoToTierAction(deviation) {
    const { t } = useTranslation();
    return {
        label: t('deviationview.tierCard.moreActionsMenu.item.goToTierPage'),
        onClick: ()=>{
            if (deviation.url) {
                window.location.href = deviation.url;
            }
        }
    };
}
function useGoToTierDashboardAction(deviation) {
    const { t } = useTranslation();
    return {
        label: t('deviationview.tierCard.moreActionsMenu.item.goToTierDashboard'),
        onClick: ()=>{
            var _deviation_tier;
            window.location.href = `${Url.buildFromPath(URLS.CUSTOMER_DASHBOARD)}?subproductid=${(_deviation_tier = deviation.tier) === null || _deviation_tier === void 0 ? void 0 : _deviation_tier.subproductId}`;
        }
    };
}
function useShareTierToPost(deviation) {
    const { t } = useTranslation();
    const dispatchShareDeviationToPost = useAction(shareDeviationToPost);
    const handleShareDeviationToPost = useCallback(()=>{
        dispatchShareDeviationToPost(deviation);
    }, [
        dispatchShareDeviationToPost,
        deviation
    ]);
    const { restrictToVerifiedAndLoggedIn } = useVerification();
    const restrictedShareToPost = restrictToVerifiedAndLoggedIn(ReferralType.SHARE_TO_POST, 'submitstatus', handleShareDeviationToPost);
    return {
        label: t('deviationview.tierCard.moreActionsMenu.item.shareInPost'),
        onClick: restrictedShareToPost
    };
}
function useShareTierToSocials(deviation) {
    const { t } = useTranslation();
    const { onCopyLinkClick } = useCopyDeviationLinkClick(deviation);
    return {
        label: t('share.copyLink'),
        onClick: onCopyLinkClick,
        biData: BiData({
            evid: 357,
            typeid: DeviationResourceType.TIER,
            itemid: deviation.deviationId,
            sectionname: BiContextProvidedValue
        })
    };
}
function useConnectDiscordAction(deviation) {
    const { t } = useTranslation();
    const dispatchSubscriberConnect = useAction(subscriberConnect);
    const isDiscordReduxInitialized = useSelector(getIsDiscordReduxInitialized);
    const handleClick = useCallback(()=>{
        dispatchSubscriberConnect(deviation);
    }, [
        dispatchSubscriberConnect,
        deviation
    ]);
    return {
        label: t('deviationview.tierCard.moreActionsMenu.item.connectDiscord'),
        onClick: handleClick,
        disabled: isDiscordReduxInitialized,
        biData: BiData({
            evid: 555,
            itemid: deviation.deviationId
        })
    };
}
function useManageTierForSubscriberAction(deviation) {
    const { t } = useTranslation();
    return {
        label: t('deviationview.tierCard.moreActionsMenu.item.unsubscribe'),
        link: Url.account(deviation.deviationId),
        biData: BiData({
            evid: 560,
            subscription_type: 'subscription'
        })
    };
}
export function useTierContextMenuItems(deviation, isOwner, studioOrdering, afterActionUrl) {
    var _deviation_tier, _deviation_tier1;
    const { isUserSubscribed } = useSubscriptionTier(deviation);
    const goToTierAction = useGoToTierAction(deviation);
    const goToTierDashboardAction = useGoToTierDashboardAction(deviation);
    const editAction = useEditTierAction(deviation);
    const deleteAction = useDeleteTierAction(deviation, afterActionUrl);
    const shareToPostAction = useShareTierToPost(deviation);
    const shareToSocialsAction = useShareTierToSocials(deviation);
    const manageTierForSubscriberAction = useManageTierForSubscriberAction(deviation);
    const connectDiscordAction = useConnectDiscordAction(deviation);
    const isDraft = ((_deviation_tier = deviation.tier) === null || _deviation_tier === void 0 ? void 0 : _deviation_tier.state) === 'draft';
    const isPendingDeletion = ((_deviation_tier1 = deviation.tier) === null || _deviation_tier1 === void 0 ? void 0 : _deviation_tier1.state) === 'pending_deletion';
    if (isOwner && !studioOrdering) {
        return [
            !isDraft && goToTierAction,
            !isDraft && goToTierDashboardAction,
            editAction,
            !isDraft && shareToPostAction,
            !isDraft && shareToSocialsAction,
            !isPendingDeletion && deleteAction
        ];
    } else if (isOwner && studioOrdering) {
        return [
            editAction,
            !isDraft && goToTierAction,
            !isDraft && goToTierDashboardAction,
            !isDraft && shareToPostAction,
            !isDraft && shareToSocialsAction,
            !isPendingDeletion && deleteAction
        ];
    } else {
        var _deviation_tier_discord, _deviation_tier2, _deviation_tier_discord1, _deviation_tier3;
        const isTierConnectedToAuthorsDiscord = !!((_deviation_tier2 = deviation.tier) === null || _deviation_tier2 === void 0 ? void 0 : (_deviation_tier_discord = _deviation_tier2.discord) === null || _deviation_tier_discord === void 0 ? void 0 : _deviation_tier_discord.connected);
        const shouldShowDiscordAction = isUserSubscribed && isTierConnectedToAuthorsDiscord && !!((_deviation_tier3 = deviation.tier) === null || _deviation_tier3 === void 0 ? void 0 : (_deviation_tier_discord1 = _deviation_tier3.discord) === null || _deviation_tier_discord1 === void 0 ? void 0 : _deviation_tier_discord1.oauth2);
        return [
            goToTierAction,
            shareToSocialsAction,
            shouldShowDiscordAction && connectDiscordAction,
            isUserSubscribed && manageTierForSubscriberAction
        ];
    }
}

//# sourceMappingURL=hooks.js.map